import { ReactNode, useEffect, useRef } from 'react'
import { View, type TamaguiElement } from 'tamagui'

interface ILoadMoreSentinelProps {
  onVisible: () => void
  threshold?: number
  children: ReactNode
}

/**
 * Sentinela de "carregar mais" da web. O InView do
 * react-native-intersection-observer decide visibilidade por posição de layout
 * relativa ao IOScrollView, e dentro de accordions aninhados (aulas de um
 * submódulo) essa medição não acompanha o conteúdo: o gatilho nunca dispara e
 * a lista congela na primeira página. O IntersectionObserver do browser mede a
 * visibilidade real do elemento através de qualquer container de scroll —
 * comprovado no painel de conteúdos em produção, onde o InView ficava mudo.
 */
export function LoadMoreSentinel({
  onVisible,
  threshold = 0.1,
  children,
}: ILoadMoreSentinelProps) {
  const ref = useRef<TamaguiElement | null>(null)
  const onVisibleRef = useRef(onVisible)
  onVisibleRef.current = onVisible

  useEffect(() => {
    // Na web o ref do View do tamagui é o próprio elemento DOM.
    const node = ref.current as unknown as Element | null
    if (!node || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) onVisibleRef.current()
        })
      },
      { threshold },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [threshold])

  return <View ref={ref}>{children}</View>
}
